/**
 * Turning a pasted link into something a venue can be called.
 *
 * Its own module rather than living in the component, because the test runner
 * does not resolve the `@/` alias that every component imports through — which
 * is the same reason `notesAutosave.ts` and `vcardStrip.ts` are separate files.
 * Pure logic goes where it can be tested.
 */

/**
 * `hacienda-san-miguel.com` → "Hacienda San Miguel".
 *
 * Pure string handling and nothing else: no request, no model. A link pasted
 * here produces a venue immediately rather than waiting on anything, and reading
 * the page for its real details is a separate, optional step.
 */
export function nameFromUrl(raw: string): { name: string; website: string } | undefined {
  const text = raw.trim();
  if (!/^https?:\/\//i.test(text) && !/^[\w-]+(\.[\w-]+)+(\/|$)/.test(text)) return undefined;

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return undefined;
  }

  // Instagram and the like put the interesting name in the path, not the host.
  const handle = /instagram\.com|facebook\.com|tiktok\.com/i.test(url.hostname)
    ? (url.pathname.split('/').filter(Boolean)[0] ?? '')
    : '';

  const source =
    handle ||
    url.hostname
      .replace(/^www\./i, '')
      .split('.')
      .slice(0, -1)
      .join(' ');

  const name = source
    .replace(/[-_.]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return name ? { name, website: url.toString() } : undefined;
}
